import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, Package, User as UserIcon } from "lucide-react";
import Layout from "@/components/Layout";
import api, { formatINR } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const STATUS_TONE = {
  pending: "bg-[#F8F6F0] text-[#163A2F]",
  paid: "bg-[#C9A227]/15 text-[#C9A227]",
  processing: "bg-[#163A2F]/10 text-[#163A2F]",
  shipped: "bg-[#163A2F] text-white",
  delivered: "bg-[#163A2F] text-[#C9A227]",
  cancelled: "bg-[#8C2A2A]/15 text-[#8C2A2A]",
};

export default function Account() {
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    api.get("/orders").then(r => setOrders(r.data || []));
  }, []);

  return (
    <Layout>
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-16">
        <div className="grid lg:grid-cols-12 gap-12">
          <aside className="lg:col-span-4">
            <div className="border hairline p-6">
              <div className="overline text-[#C9A227]">Welcome</div>
              <div className="mt-2 text-2xl font-light text-[#163A2F]">{user?.name}</div>
              <div className="text-sm text-[#163A2F]/60">{user?.email}</div>
              <div className="my-5 gold-rule" />
              <nav className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-[#163A2F]"><UserIcon className="w-4 h-4" /> Profile</div>
                <div className="flex items-center gap-2 text-[#163A2F]"><Package className="w-4 h-4" /> Orders ({orders.length})</div>
                {user?.role === "admin" && (
                  <Link to="/admin" data-testid="admin-link" className="block mt-4 text-[#C9A227] underline">Open admin dashboard →</Link>
                )}
                <button onClick={logout} data-testid="logout-button" className="mt-6 flex items-center gap-2 text-[#8C2A2A] text-sm">
                  <LogOut className="w-4 h-4" /> Sign out
                </button>
              </nav>
            </div>
          </aside>

          <div className="lg:col-span-8">
            <div className="overline text-[#C9A227]">Your orders</div>
            <h1 className="mt-2 text-3xl md:text-4xl font-light text-[#163A2F]">Order history</h1>

            {orders.length === 0 ? (
              <div className="mt-10 border hairline p-12 text-center text-[#163A2F]/60">
                You haven't placed any orders yet.
                <div className="mt-6"><Link to="/shop" className="bg-[#163A2F] text-white px-6 py-3 uppercase text-xs tracking-[0.25em]">Start shopping</Link></div>
              </div>
            ) : (
              <div className="mt-8 space-y-5">
                {orders.map(o => (
                  <div key={o.id} className="border hairline p-5 md:p-6" data-testid={`order-${o.order_number}`}>
                    <div className="flex flex-wrap justify-between gap-3 items-center">
                      <div>
                        <div className="overline text-[#C9A227]">Order</div>
                        <div className="text-[#163A2F] font-medium">{o.order_number}</div>
                      </div>
                      <div className="text-sm text-[#163A2F]/60">{new Date(o.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</div>
                      <div className={`text-[10px] uppercase tracking-[0.2em] px-3 py-1 ${STATUS_TONE[o.status] || "bg-[#163A2F]/5 text-[#163A2F]"}`}>{o.status}</div>
                      <div className="text-[#163A2F] font-medium">{formatINR(o.total)}</div>
                    </div>
                    <div className="mt-4 flex gap-3 overflow-x-auto">
                      {o.items.map((it, idx) => (
                        <img key={idx} src={it.image} alt={it.name} className="w-16 h-20 object-cover bg-[#163A2F]/5 flex-shrink-0" />
                      ))}
                    </div>
                    {o.tracking_number && (
                      <div className="mt-4 text-sm text-[#163A2F]/80">
                        Tracking: <span className="font-medium">{o.tracking_number}</span>{o.courier && ` · ${o.courier}`}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
